import React, { useEffect, useState } from 'react';
import { DollarSign, Plus, Edit2, X, Trash2, Search, Layers3, Check, Power } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';
import { AdminConfirmDialog } from '../../components/AdminUi';

const FEATURE_DEFINITIONS = [
  { key: 'monteCarlo', label: 'Monte Carlo' },
  { key: 'pdfReports', label: 'PDF Reports' },
  { key: 'brokerComparison', label: 'Broker Compare' },
  { key: 'prioritySupport', label: 'Priority Support' },
];

const EMPTY_PLAN = {
  slug: '',
  name: '',
  priceMonthly: 0,
  priceYearly: 0,
  maxAccounts: 0,
  maxApiKeys: 0,
  dataRetentionDays: 0,
  isActive: true,
  sortOrder: 0,
  features: {
    monteCarlo: false,
    pdfReports: false,
    brokerComparison: false,
    prioritySupport: false,
  },
};

const normalizePlan = (plan = {}) => ({
  ...EMPTY_PLAN,
  ...plan,
  priceMonthly: Number(plan.priceMonthly || 0),
  priceYearly: Number(plan.priceYearly || 0),
  maxAccounts: Number(plan.maxAccounts || 0),
  maxApiKeys: Number(plan.maxApiKeys || 0),
  dataRetentionDays: Number(plan.dataRetentionDays || 0),
  sortOrder: Number(plan.sortOrder || 0),
  isActive: plan.isActive !== false,
  features: { ...EMPTY_PLAN.features, ...(plan.features || {}) },
});

export default function AdminPricing() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [newPlan, setNewPlan] = useState(EMPTY_PLAN);
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const data = await apiGet(API_ROUTES.admin.plans);
      setPlans(data.plans || []);
    } catch (error) {
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const startCreate = () => {
    setNewPlan({ ...EMPTY_PLAN, features: { ...EMPTY_PLAN.features } });
    setCreating(true);
  };

  const startEdit = (plan) => {
    setEditingId(plan.id);
    setEditForm(normalizePlan(plan));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editForm.slug || !editForm.name) {
      toast.error('Slug and name are required');
      return;
    }

    try {
      const res = await apiFetch(`${API_ROUTES.admin.plans}/${editingId}`, {
        method: 'PATCH',
        body: editForm,
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Plan updated');
      cancelEdit();
      fetchPlans();
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const createPlan = async () => {
    if (!newPlan.slug || !newPlan.name) {
      toast.error('Slug and name are required');
      return;
    }

    try {
      const res = await apiFetch(API_ROUTES.admin.plans, {
        method: 'POST',
        body: newPlan,
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Plan created');
      setCreating(false);
      setNewPlan({ ...EMPTY_PLAN, features: { ...EMPTY_PLAN.features } });
      fetchPlans();
    } catch (error) {
      toast.error('Failed to create plan');
    }
  };

  const requestDeletePlan = (plan, hardDelete = false) => {
    setPendingDelete({ plan, hardDelete });
  };

  const deletePlan = async () => {
    if (!pendingDelete) return;
    const { plan, hardDelete } = pendingDelete;
    try {
      const res = await apiFetch(`${API_ROUTES.admin.plans}/${plan.id}`, {
        method: hardDelete ? 'DELETE' : 'PATCH',
        body: hardDelete ? undefined : { isActive: false },
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(hardDelete ? 'Plan deleted permanently' : 'Plan deactivated');
      setPendingDelete(null);
      fetchPlans();
    } catch (error) {
      toast.error(hardDelete ? 'Failed to delete plan' : 'Failed to deactivate plan');
    }
  };

  const normalizedSearch = search.trim().toLowerCase();
  const visiblePlans = plans.filter((plan) => {
    const matchesSearch = !normalizedSearch
      || plan.name?.toLowerCase().includes(normalizedSearch)
      || plan.slug?.toLowerCase().includes(normalizedSearch);
    const matchesStatus = statusFilter === 'all'
      || (statusFilter === 'active' && plan.isActive)
      || (statusFilter === 'inactive' && !plan.isActive);
    return matchesSearch && matchesStatus;
  });

  const activePlans = plans.filter((plan) => plan.isActive);
  const paidPlans = plans.filter((plan) => Number(plan.priceMonthly || 0) > 0);
  const averageMonthly = paidPlans.length
    ? paidPlans.reduce((sum, plan) => sum + Number(plan.priceMonthly || 0), 0) / paidPlans.length
    : 0;
  const topPlan = [...plans].sort((a, b) => Number(b.priceMonthly || 0) - Number(a.priceMonthly || 0))[0];
  const editingPlanName = plans.find((plan) => plan.id === editingId)?.name || editForm.name;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-100">Pricing Management</h1>
          <p className="text-sm text-gray-500 mt-1">Configure plan prices, features, and limits</p>
        </div>
        <button
          onClick={startCreate}
          className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all text-sm font-medium"
        >
          <Plus size={16} />
          Create Plan
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <SummaryCard label="Active Plans" value={activePlans.length} sub={`${plans.length} total configured`} />
        <SummaryCard label="Avg Paid Monthly" value={`$${averageMonthly.toFixed(2)}`} sub={paidPlans.length ? `${paidPlans.length} paid plans` : 'No paid plans yet'} />
        <SummaryCard label="Highest Monthly" value={topPlan ? `$${Number(topPlan.priceMonthly || 0).toFixed(2)}` : '$0.00'} sub={topPlan ? `${topPlan.name} (${topPlan.slug})` : 'No plans yet'} />
      </div>

      <div className="glass-card bg-dark-card p-4 mb-6 flex flex-col md:flex-row md:items-center gap-3 md:justify-between">
        <div className="relative w-full md:max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search plans by name or slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-dark-surface border border-dark-border rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
          />
        </div>
        <div className="flex items-center gap-2">
          {['all', 'active', 'inactive'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition ${
                statusFilter === status
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-gray-200'
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {creating && (
        <PlanModal
          mode="create"
          form={newPlan}
          setForm={setNewPlan}
          onSave={createPlan}
          onClose={() => setCreating(false)}
        />
      )}

      {editingId && (
        <PlanModal
          mode="edit"
          title={`Edit ${editingPlanName}`}
          form={editForm}
          setForm={setEditForm}
          onSave={saveEdit}
          onClose={cancelEdit}
        />
      )}

      <div className="glass-card bg-dark-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-dark-border text-gray-500 text-xs uppercase">
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-right px-4 py-3">Monthly</th>
                <th className="text-right px-4 py-3">Yearly</th>
                <th className="text-center px-4 py-3">Accounts</th>
                <th className="text-center px-4 py-3">API Keys</th>
                <th className="text-left px-4 py-3">Features</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-center px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">Loading...</td></tr>
              ) : !visiblePlans.length ? (
                <tr><td colSpan={8} className="text-center py-12 text-gray-500">No plans match the current filters</td></tr>
              ) : (
                visiblePlans.map((plan) => {
                  const enabledFeatures = FEATURE_DEFINITIONS.filter((feature) => plan.features?.[feature.key]);
                  const savingsPct = getSavingsPct(plan.priceMonthly, plan.priceYearly);

                  return (
                    <tr key={plan.id} className="border-b border-dark-border/50 hover:bg-white/[0.02]">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-gray-200 font-medium">{plan.name}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <p className="text-gray-500 text-xs font-mono">{plan.slug}</p>
                            <span className="text-[10px] text-gray-600 border border-dark-border rounded-full px-2 py-0.5">
                              {formatRetention(plan.dataRetentionDays)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div>
                          <span className="text-gray-300 font-medium">${Number(plan.priceMonthly || 0).toFixed(2)}</span>
                          <p className="text-[10px] text-gray-600 mt-1">per month</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div>
                          <span className="text-gray-300 font-medium">${Number(plan.priceYearly || 0).toFixed(2)}</span>
                          <p className="text-[10px] text-gray-600 mt-1">
                            {savingsPct > 0 ? `${savingsPct}% yearly savings` : 'no yearly discount'}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-400">{formatLimit(plan.maxAccounts)}</td>
                      <td className="px-4 py-3 text-center text-gray-400">{formatLimit(plan.maxApiKeys)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {enabledFeatures.length ? enabledFeatures.map((feature) => (
                            <span key={feature.key} className="inline-flex items-center gap-1 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-300">
                              <Layers3 size={10} />
                              {feature.label}
                            </span>
                          )) : (
                            <span className="text-xs text-gray-500">Core tracking only</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="space-y-1">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs ${plan.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
                            {plan.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <p className="text-[10px] text-gray-600">{enabledFeatures.length} features enabled</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => startEdit(plan)} className="text-blue-400 hover:text-blue-300" title="Edit plan">
                            <Edit2 size={16} />
                          </button>
                          {plan.isActive ? (
                            <button onClick={() => requestDeletePlan(plan, false)} className="text-yellow-400 hover:text-yellow-300" title="Deactivate plan">
                              <Trash2 size={16} />
                            </button>
                          ) : (
                            <button onClick={() => requestDeletePlan(plan, true)} className="text-red-400 hover:text-red-300" title="Permanently delete plan">
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <AdminConfirmDialog
        open={Boolean(pendingDelete)}
        tone={pendingDelete?.hardDelete ? 'danger' : 'warning'}
        title={pendingDelete?.hardDelete ? 'Permanently delete plan?' : 'Deactivate plan?'}
        message={pendingDelete ? `${pendingDelete.plan.name} (${pendingDelete.plan.slug}) will ${pendingDelete.hardDelete ? 'be permanently deleted. This cannot be undone.' : 'be hidden from new purchases. Existing users keep access until expiry.'}` : ''}
        confirmLabel={pendingDelete?.hardDelete ? 'Delete Permanently' : 'Deactivate Plan'}
        requireText={pendingDelete?.hardDelete ? pendingDelete.plan.slug : ''}
        onConfirm={deletePlan}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

function PlanModal({ mode, title, form, setForm, onSave, onClose }) {
  const isEdit = mode === 'edit';
  const monthly = Number(form.priceMonthly || 0);
  const yearly = Number(form.priceYearly || 0);
  const savingsPct = getSavingsPct(monthly, yearly);
  const enabledCount = FEATURE_DEFINITIONS.filter((feature) => form.features?.[feature.key]).length;

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateNumber = (field, value) => update(field, value === '' ? 0 : Number(value));
  const updateFeature = (key, checked) => setForm((prev) => ({
    ...prev,
    features: { ...prev.features, [key]: checked },
  }));

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-dark-card border border-dark-border rounded-xl w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-dark-border px-6 py-5">
          <div>
            <h2 className="text-lg font-bold text-gray-100">{title || (isEdit ? 'Edit Plan' : 'Create New Plan')}</h2>
            <p className="text-xs text-gray-500 mt-1">
              {isEdit ? 'Update pricing, limits, feature access, and availability.' : 'Configure a new subscription plan for checkout and access control.'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-200 rounded-lg p-1" title="Close">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(92vh-150px)] px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-5">
            <Section title="Plan Identity">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextField label="Slug" value={form.slug} placeholder="elite" onChange={(value) => update('slug', value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} />
                <TextField label="Display Name" value={form.name} placeholder="Elite Plan" onChange={(value) => update('name', value)} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <NumberField label="Sort Order" value={form.sortOrder} min="0" onChange={(value) => updateNumber('sortOrder', value)} />
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Status</label>
                  <button
                    type="button"
                    onClick={() => update('isActive', !form.isActive)}
                    className={`w-full h-[38px] rounded-lg border px-3 text-sm font-medium flex items-center justify-center gap-2 transition ${
                      form.isActive
                        ? 'border-emerald-500/30 bg-emerald-500/15 text-emerald-300'
                        : 'border-dark-border bg-dark-surface text-gray-400'
                    }`}
                  >
                    <Power size={14} />
                    {form.isActive ? 'Active' : 'Inactive'}
                  </button>
                </div>
              </div>
            </Section>

            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-4">
              <p className="text-xs uppercase tracking-wide text-emerald-300 font-semibold">Preview</p>
              <p className="text-xl font-bold text-gray-100 mt-3">{form.name || 'Plan name'}</p>
              <p className="text-xs text-gray-500 font-mono mt-1">{form.slug || 'plan-slug'}</p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <PreviewMetric label="Monthly" value={`$${monthly.toFixed(2)}`} />
                <PreviewMetric label="Yearly" value={`$${yearly.toFixed(2)}`} />
                <PreviewMetric label="Accounts" value={formatLimit(form.maxAccounts)} />
                <PreviewMetric label="Features" value={enabledCount} />
              </div>
              <p className="text-xs text-gray-500 mt-4">{savingsPct > 0 ? `${savingsPct}% yearly savings` : 'No yearly discount configured'}</p>
            </div>
          </div>

          <Section title="Pricing (USD)">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <MoneyField label="Monthly Price" value={form.priceMonthly} placeholder="9.99" onChange={(value) => updateNumber('priceMonthly', value)} />
              <MoneyField label="Yearly Price" value={form.priceYearly} placeholder="99.99" onChange={(value) => updateNumber('priceYearly', value)} />
            </div>
          </Section>

          <Section title="Limits & Quotas">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <NumberField label="Max Trading Accounts" value={form.maxAccounts} placeholder="5" min="0" onChange={(value) => updateNumber('maxAccounts', value)} />
              <NumberField label="Max API Keys" value={form.maxApiKeys} placeholder="3" min="0" onChange={(value) => updateNumber('maxApiKeys', value)} />
              <NumberField label="Data Retention (days)" value={form.dataRetentionDays} placeholder="90" min="0" onChange={(value) => updateNumber('dataRetentionDays', value)} />
            </div>
          </Section>

          <Section title="Features Included">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FEATURE_DEFINITIONS.map((feature) => {
                const checked = form.features?.[feature.key] || false;
                return (
                  <button
                    key={feature.key}
                    type="button"
                    onClick={() => updateFeature(feature.key, !checked)}
                    className={`flex items-center justify-between rounded-lg border px-3 py-3 text-left transition ${
                      checked
                        ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
                        : 'border-dark-border bg-dark-surface/70 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    <span className="text-sm font-medium">{feature.label}</span>
                    <span className={`h-5 w-5 rounded-full border flex items-center justify-center ${checked ? 'border-emerald-400 bg-emerald-500 text-white' : 'border-gray-600'}`}>
                      {checked && <Check size={12} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </Section>
        </div>

        <div className="border-t border-dark-border px-6 py-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-dark-surface border border-dark-border text-gray-300 hover:text-white text-sm font-medium">
            Cancel
          </button>
          <button onClick={onSave} className="px-5 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 text-sm font-semibold">
            {isEdit ? 'Save Changes' : 'Create Plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-dark-border bg-dark-surface/30 p-4">
      <p className="text-xs text-gray-400 uppercase font-semibold mb-3">{title}</p>
      {children}
    </div>
  );
}

function TextField({ label, value, placeholder, onChange }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        type="text"
        placeholder={placeholder}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 w-full focus:outline-none focus:border-emerald-500/40"
      />
    </div>
  );
}

function NumberField({ label, value, placeholder, min, onChange }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <input
        type="number"
        placeholder={placeholder}
        value={value === 0 ? '' : value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200 w-full focus:outline-none focus:border-emerald-500/40"
      />
    </div>
  );
}

function MoneyField({ label, value, placeholder, onChange }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      <div className="flex items-center rounded-lg border border-dark-border bg-dark-surface focus-within:border-emerald-500/40">
        <span className="text-gray-500 px-3">$</span>
        <input
          type="number"
          placeholder={placeholder}
          value={value === 0 ? '' : value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          step="0.01"
          min="0"
          className="bg-transparent px-0 py-2 pr-3 text-sm text-gray-200 w-full focus:outline-none"
        />
      </div>
    </div>
  );
}

function PreviewMetric({ label, value }) {
  return (
    <div className="rounded-lg bg-dark-surface border border-dark-border p-3">
      <p className="text-[10px] uppercase text-gray-600 font-semibold">{label}</p>
      <p className="text-sm text-gray-200 font-semibold mt-1">{value}</p>
    </div>
  );
}

function SummaryCard({ label, value, sub }) {
  return (
    <div className="glass-card bg-dark-card p-4">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-100 mt-2">{value}</p>
      <p className="text-xs text-gray-600 mt-1">{sub}</p>
    </div>
  );
}

function formatLimit(value) {
  return Number(value) >= 999999 ? 'Unlimited' : String(value ?? 0);
}

function formatRetention(days) {
  return Number(days) >= 999999 ? 'Unlimited retention' : `${days || 0} day retention`;
}

function getSavingsPct(monthly, yearly) {
  const monthlyValue = Number(monthly || 0);
  const yearlyValue = Number(yearly || 0);
  if (monthlyValue <= 0 || yearlyValue <= 0) return 0;
  return Math.max(0, Math.round((1 - yearlyValue / (monthlyValue * 12)) * 100));
}